import React, { useState, useEffect } from "react";
import {
  doc,
  updateDoc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  getDoc,
  query,
  where,
  addDoc,
} from "firebase/firestore";
import DOMPurify from "dompurify";
import pluralize from "pluralize";
import { db } from "../firebase";
import TipTap from "../components/TipTap";
import xMarkIcon from "../assets/icons/xmark.svg";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import loadingSpinner from "../assets/icons/loading_spinner.svg";
import { Switch } from "@mui/material";

interface Template {
  id: string;
  displayName: string;
  description: string;
  visiblePrompt: string;
  hiddenPrompt: string;
  createdAt?: Date | null;
}

interface Templates {
  clinicalNotes: Template[];
  letters: Template[];
  meetingNotes: Template[];
}

const TemplatesTabContent: React.FC<{ userID: string }> = ({ userID }) => {
  const [presetTemplates, setPresetTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [userTemplates, setUserTemplates] = useState<Templates>({
    clinicalNotes: [],
    letters: [],
    meetingNotes: [],
  });
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [newTemplateName, setNewTemplateName] = useState<string>("");
  const [newTemplateDescription, setNewTemplateDescription] =
    useState<string>("");
  const [newTemplateContent, setNewTemplateContent] = useState<string>("");
  const [newTemplateCategory, setNewTemplateCategory] = useState<
    keyof Templates | null
  >(null);
  const [isNewTemplateModalOpen, setIsNewTemplateModalOpen] =
    useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [isEditingTemplateName, setIsEditingTemplateName] = useState<
    string | null
  >(null);
  const [isEditingTemplateDescription, setIsEditingTemplateDescription] =
    useState<string | null>(null);
  const [isEditingTemplateContent, setIsEditingTemplateContent] = useState<
    string | null
  >(null);
  const [
    isDeleteTemplateConfirmationModalOpen,
    setIsDeleteTemplateConfirmationModalOpen,
  ] = useState<boolean>(false);
  const [loadingOptimisePrompt, setLoadingOptimisePrompt] =
    useState<boolean>(false);
  const [defaultTemplateCategory, setDefaultTemplateCategory] = useState<
    string | null
  >(null);
  const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(
    null
  );
  const [defaultTemplateIsPreset, setDefaultTemplateIsPreset] =
    useState<boolean>(false);
  const [loadingSaveTemplate, setLoadingSaveTemplate] =
    useState<boolean>(false);
  const [loadingDeleteTemplate, setLoadingDeleteTemplate] =
    useState<boolean>(false);
  const [examples, setExamples] = useState<string[]>([]);

  useEffect(() => {
    const fetchDefaultTemplate = async () => {
      setPageLoading(true);
      const userRef = doc(db, "users", userID);
      const userSnapshot = await getDoc(userRef);
      if (userSnapshot.exists()) {
        const userData = userSnapshot.data();
        setDefaultTemplateCategory(userData.defaultTemplateCategory);
        setDefaultTemplateId(userData.defaultTemplateId);
        setDefaultTemplateIsPreset(userData.defaultTemplateIsPreset);
      }
      setPageLoading(false);
    };
    const fetchTemplates = async () => {
      setPageLoading(true);
      const templateCategories = ["clinicalNotes", "letters", "meetingNotes"];
      let fetchedPresetTemplates: Templates = {
        clinicalNotes: [],
        letters: [],
        meetingNotes: [],
      };
      let fetchedUserTemplates: Templates = {
        clinicalNotes: [],
        letters: [],
        meetingNotes: [],
      };

      const fetchCategoryTemplates = async (
        docRef: any,
        category: keyof Templates,
        templates: Templates
      ) => {
        const categoryCollectionRef = collection(docRef, category);
        const categoryQuerySnapshot = await getDocs(categoryCollectionRef);

        const fetchedTemplates: Template[] = [];

        categoryQuerySnapshot.forEach((docSnapshot) => {
          if (docSnapshot.exists()) {
            const typeData = docSnapshot.data();
            const template: Template = {
              id: docSnapshot.id,
              displayName: typeData.displayName || "",
              description: typeData.description || "",
              visiblePrompt: typeData.visiblePrompt || "",
              hiddenPrompt: typeData.hiddenPrompt || "",
              createdAt: typeData.createdAt
                ? new Date(typeData.createdAt)
                : null,
            };

            fetchedTemplates.push(template);
          }
        });

        // Sort the fetched templates by createdAt if it exists
        fetchedTemplates.sort((a, b) => {
          const aCreatedAt = a.createdAt ? a.createdAt.getTime() : 0;
          const bCreatedAt = b.createdAt ? b.createdAt.getTime() : 0;
          return aCreatedAt - bCreatedAt;
        });

        templates[category] = fetchedTemplates;
      };

      // Create an array of promises for fetching templates
      const promises = templateCategories.flatMap((category) => [
        fetchCategoryTemplates(
          doc(db, "moticsTemplates", "current"),
          category as keyof Templates,
          fetchedPresetTemplates
        ).catch((error) =>
          console.error("Error fetching preset templates:", error)
        ),
        fetchCategoryTemplates(
          doc(db, "users", userID, "templates", "current"),
          category as keyof Templates,
          fetchedUserTemplates
        ).catch((error) =>
          console.error("Error fetching user templates:", error)
        ),
      ]);

      // Wait for all promises to resolve before updating state
      await Promise.all(promises);
      setPresetTemplates(fetchedPresetTemplates);
      setUserTemplates(fetchedUserTemplates);
      setPageLoading(false);
    };

    fetchDefaultTemplate();
    fetchTemplates();
  }, [userID]);

  function camelCaseToWords(str: string): string {
    // Split the string into words based on capital letters.
    const words = str.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])/g) || [];

    // Capitalize the first word and join the rest of the words with spaces.
    const result = words
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          : word
      )
      .join(" ");

    return result;
  }

  const checkExistingTemplate = async (newTemplateId: string) => {
    if (newTemplateCategory) {
      const userTemplateRef = collection(
        db,
        "users",
        userID,
        "templates",
        "current",
        newTemplateCategory
      );
      const templateSnapshot = await getDocs(userTemplateRef);

      if (templateSnapshot.docs.some((doc) => doc.id === newTemplateId)) {
        return true;
      }
    }

    return false; // Return false if template with the same ID does not exist
  };

  const handleSetDefaultTemplate = async (
    category: keyof Templates,
    templateId: string,
    templateDisplayName: string
  ) => {
    // Reference to the new user template
    const userRef = doc(db, "users", userID);
    const isPresetTemplate = presetTemplates[category].some(
      (presetTemplate) => presetTemplate.id === templateId
    );

    try {
      // Check if trying to deselect the current default template
      if (
        defaultTemplateCategory === category &&
        defaultTemplateId === templateId
      ) {
        // Reset the default template
        await updateDoc(userRef, {
          defaultTemplateCategory: null,
          defaultTemplateId: null,
          defaultTemplateIsPreset: false,
        });
        setDefaultTemplateCategory("");
        setDefaultTemplateId("");
        setDefaultTemplateIsPreset(false);
        alert("Default template has been deselected.");
      } else {
        await updateDoc(userRef, {
          defaultTemplateCategory: category,
          defaultTemplateId: templateId,
          defaultTemplateIsPreset: isPresetTemplate,
        });
        setDefaultTemplateCategory(category);
        setDefaultTemplateId(templateId);
        setDefaultTemplateIsPreset(isPresetTemplate);
        alert(`${templateDisplayName} has been set as the default template`);
      }
    } catch (error) {
      console.error("Error updating default template:", error);
      alert("Failed to update default template. Please try again.");
    }
  };

  const addNewTemplate = async () => {
    if (newTemplateName) {
      setLoadingSaveTemplate(true);
      const newTemplateId = newTemplateName
        .replace(/ /g, "")
        .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase())
        .replace(/(^\w{1})/g, (letter) => letter.toLowerCase());

      const templateExists = await checkExistingTemplate(newTemplateId);

      if (!templateExists) {
        const newTemplate: Template = {
          id: newTemplateId,
          displayName: newTemplateName,
          description: newTemplateDescription,
          visiblePrompt: newTemplateContent,
          hiddenPrompt: "",
        };
        if (newTemplateCategory) {
          // console.log("newTemplateCategory", newTemplateCategory);
          setUserTemplates((prevUserTemplates) => ({
            ...prevUserTemplates,
            [newTemplateCategory]: [
              ...prevUserTemplates[newTemplateCategory],
              newTemplate,
            ],
          }));

          const userTemplateRef = doc(
            db,
            "users",
            userID,
            "templates",
            "current",
            newTemplateCategory,
            newTemplateId
          );
          try {
            const timestamp = new Date();
            await setDoc(userTemplateRef, {
              displayName: newTemplateName,
              description: newTemplateDescription,
              visiblePrompt: newTemplateContent,
              createdAt: timestamp,
            });

            // Create the "Examples" collection and add each example as a document
            const examplesCollectionRef = collection(
              userTemplateRef,
              "examples"
            );
            for (const [index, example] of examples.entries()) {
              if (example.trim() !== "") { // Check if the example is not blank
                const exampleDocRef = doc(examplesCollectionRef, `example${index + 1}`);
                await setDoc(exampleDocRef, {
                  content: example,
                  createdAt: timestamp,
                });
              }
            }

            alert("New template has been successfully added!");
            setLoadingSaveTemplate(false);
            setNewTemplateName("");
            setNewTemplateDescription("");
            setNewTemplateContent("");
            setIsNewTemplateModalOpen(false);
            setExamples([]);
          } catch (error) {
            console.error("Error adding new template:", error);
            alert("Failed to add new template. Please try again.");
          }
        }
      } else {
        if (newTemplateCategory) {
          alert(
            `A template with the same name already exists in ${camelCaseToWords(
              newTemplateCategory
            )}. Please choose a different name.`
          );
        } else {
          alert(
            "A template with the same name already exists. Please choose a different name."
          );
        }
        setLoadingSaveTemplate(false);
      }
    }
  };

  const toggleExpand = (templateName: string) => {
    setExpandedTemplate((prev) =>
      prev === templateName ? null : templateName
    );
  };

  const saveEditTemplateName = async (
    category: keyof Templates,
    id: string,
    newName: string
  ) => {
    const updatedTemplates = userTemplates[category].map((userTemplate) =>
      userTemplate.id === id
        ? { ...userTemplate, displayName: newName }
        : userTemplate
    );

    setUserTemplates((prevUserTemplates) => ({
      ...prevUserTemplates,
      [category]: updatedTemplates,
    }));

    const userTemplatesRef = doc(
      db,
      "users",
      userID,
      "templates",
      "current",
      category,
      id
    );
    await updateDoc(userTemplatesRef, {
      displayName: newName,
    });
  };

  const saveEditTemplateDescription = async (
    category: keyof Templates,
    id: string,
    newDescription: string
  ) => {
    const updatedTemplates = userTemplates[category].map((userTemplate) =>
      userTemplate.id === id
        ? { ...userTemplate, description: newDescription }
        : userTemplate
    );

    setUserTemplates((prevUserTemplates) => ({
      ...prevUserTemplates,
      [category]: updatedTemplates,
    }));

    const userTemplatesRef = doc(
      db,
      "users",
      userID,
      "templates",
      "current",
      category,
      id
    );
    await updateDoc(userTemplatesRef, {
      description: newDescription,
    });
  };

  const saveEditTemplateContent = async (
    category: keyof Templates,
    id: string,
    name: string,
    newContent: string
  ) => {
    const currentContent = userTemplates[category].find((t) => t.id === id);
    if (!currentContent) return;

    const updatedTemplates = userTemplates[category].map((userTemplate) =>
      userTemplate.id === id
        ? { ...userTemplate, visiblePrompt: newContent }
        : userTemplate
    );

    setUserTemplates((prevUserTemplates) => ({
      ...prevUserTemplates,
      [category]: updatedTemplates,
    }));

    const userTemplatesRef = doc(
      db,
      "users",
      userID,
      "templates",
      "current",
      category,
      id
    );
    await updateDoc(userTemplatesRef, {
      visiblePrompt: newContent,
    });
  };

  const deleteTemplate = async (category: keyof Templates, id: string) => {
    setLoadingDeleteTemplate(true);
    const updatedTemplates = userTemplates[category].filter(
      (userTemplate) => userTemplate.id !== id
    );

    setUserTemplates((prevUserTemplates) => ({
      ...prevUserTemplates,
      [category]: updatedTemplates,
    }));

    const userTemplatesRef = doc(
      db,
      "users",
      userID,
      "templates",
      "current",
      category,
      id
    );
    try {
      await deleteDoc(userTemplatesRef);
      alert("Template has been successfully deleted!");
      setLoadingDeleteTemplate(false);
      setIsDeleteTemplateConfirmationModalOpen(false);
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template. Please try again.");
      setLoadingDeleteTemplate(false);
    }
  };

  const handleSaveClick = () => {
    if (newTemplateName === "" || newTemplateContent === "") {
      alert("Name and template/ instructions are required.");
    } else {
      addNewTemplate();
    }
  };

  const handleCloseNewTemplateModal = () => {
    setNewTemplateName("");
    setNewTemplateDescription("");
    setNewTemplateContent("");
    setIsNewTemplateModalOpen(false);
    setExamples([]);
  };

  const handleOptimisePrompt = async () => {
    setLoadingOptimisePrompt(true);
    const userPrompt = newTemplateContent;
    console.log("User prompt:", userPrompt);
    try {
      const response = await fetch(
        "https://europe-west2-motics-consultations.cloudfunctions.net/template-improve-prompt",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userPrompt: userPrompt }),
        }
      );
      const data = await response.json();
      console.log("Optimised prompt: ", data);
      setLoadingOptimisePrompt(false);
      return data;
    } catch (error) {
      console.error("Error optimizing prompt:", error);
      setLoadingOptimisePrompt(false);
      return null;
    }
  };

  const addExample = () => {
    if (examples.length >= 3) {
      alert("You can only add up to 3 examples.");
      return;
    }
    setExamples([...examples, ""]);
    // TODO: Add a check to see if the template content is empty and if it is, add the example to the template content
    // TODO: Plug backend endpoints
  };

  const updateExample = (index: number, value: string) => {
    const newExamples = examples.map((content, i) =>
      i === index ? value : content
    );
    setExamples(newExamples);
  };

  const removeExample = (index: number) => {
    const filteredExamples = examples.filter((_, i) => i !== index);
    setExamples(filteredExamples);
  };

  const renderAddNewTemplate = () => {
    return (
      <div className="box-sizing-default modal-background">
        <div className="box-sizing-default modals" id="assign-patient-modal">
          <button
            className="small-icon-button"
            onClick={() => {
              handleCloseNewTemplateModal();
            }}
          >
            <img src={xMarkIcon} alt="close" />
          </button>
          <div
            style={{
              fontSize: "2rem",
              fontWeight: "500",
              marginBottom: "1rem",
            }}
          >
            Add New Template
          </div>
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "500",
              marginBottom: "2rem",
            }}
          >
            {camelCaseToWords(pluralize.singular(newTemplateCategory || ""))}
          </div>
          <div className="new-template-form">
            <label htmlFor="add-template-name">Name</label>
            <input
              id="add-template-name"
              type="text"
              placeholder="Enter the name for your new template"
              value={newTemplateName}
              onChange={(event) => setNewTemplateName(event.target.value)}
              required
            />
            <label htmlFor="add-template-description">Description</label>
            <input
              id="add-template-description"
              type="text"
              placeholder="Enter the description for your new template"
              value={newTemplateDescription}
              onChange={(event) =>
                setNewTemplateDescription(event.target.value)
              }
            />
            <label htmlFor="add-template-content">Instructions/ Examples</label>
            <div style={{ position: "relative" }}>
              <textarea
                id="add-template-content"
                placeholder={`Enter your instructions or paste your note/ letter examples here.\n\nExample: "Generate a letter for private insurance with information about the patient's diagnosis, plan, and request for reimbursement."`}
                value={newTemplateContent}
                onChange={(event) => setNewTemplateContent(event.target.value)}
                rows={10}
                style={{ resize: "none" }}
                disabled={loadingOptimisePrompt}
                required
              />
              {/* {examples.map((example, index) => (
                <div
                  key={index}
                  style={{ marginTop: "10px", marginBottom: "0" }}
                >
                  <label htmlFor={`add-template-content-${index}`}>
                    Example {index + 1}
                  </label>
                  <textarea
                    placeholder="Paste an example note or letter here."
                    value={example}
                    onChange={(e) => updateExample(index, e.target.value)}
                    rows={5}
                    style={{ display: "block", width: "100%", resize: "none" }}
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      className="button-add-symbol"
                      onClick={() => removeExample(index)}
                    >
                      <span className="plus-symbol">➖</span>Remove Example
                    </button>
                  </div>
                </div>
              ))}
              <button
                className="button-add-symbol"
                style={{ marginTop: "1rem" }}
                onClick={addExample}
              >
                <span className="plus-symbol">➕</span>
                Add example
              </button> */}
              <button
                className="button-copy"
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "auto",
                  color: "white",
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                }}
                onClick={async () => {
                  if (newTemplateContent.trim() === "") {
                    alert("Template content cannot be empty.");
                  } else {
                    const optimisedPrompt = await handleOptimisePrompt();
                    if (optimisedPrompt) {
                      setNewTemplateContent(optimisedPrompt);
                    } else {
                      alert("Failed to optimize prompt. Please try again.");
                    }
                  }
                }}
              >
                {loadingOptimisePrompt ? (
                  <img
                    src={loadingSpinner}
                    alt="Loading"
                    style={{
                      width: "1.5rem",
                      marginRight: "0.8rem",
                    }}
                  />
                ) : (
                  <AutoFixHighIcon sx={{ mr: 1 }} />
                )}
                Optimise with AI
              </button>
            </div>
            {/* <TipTap
              content={DOMPurify.sanitize(newTemplateContent)}
              alwaysFocus={true}
              placeholder="Enter the content for your new template"
              onSaveContent={(newContent) => {
                setNewTemplateContent(newContent);
              }}
            /> */}
            {/* <button
              className="button-small-brand-green"
              style={{ marginTop: "2rem" }}
              onClick={() => handleSaveClick()}
            >
              Save
            </button> */}
            <button
              className="button-small-brand-green"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "2rem",
                cursor: loadingSaveTemplate ? "not-allowed" : "pointer",
              }}
              onClick={() => handleSaveClick()}
              disabled={loadingSaveTemplate}
            >
              {loadingSaveTemplate && (
                <img
                  src={loadingSpinner}
                  alt="Loading"
                  style={{
                    width: "1.5rem",
                    marginRight: "0.8rem",
                  }}
                />
              )}
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTemplatesList = (category: keyof Templates) => {
    // Concatenate presetTemplates and userTemplates for the given category
    const combinedTemplates = [
      ...presetTemplates[category],
      ...userTemplates[category],
    ];
    if (combinedTemplates.length === 0) {
      return (
        <div
          style={{
            fontSize: "1rem",
            fontStyle: "italic",
            marginBottom: "1rem",
          }}
        >
          No templates available.
        </div>
      );
    }

    return combinedTemplates.map((template) => {
      const templateName = template.displayName;
      let highlightedTemplateName = templateName.includes("[Motics Preset]")
        ? templateName.replace(
            "[Motics Preset]",
            '<span class="motics-preset">[Motics Preset]</span>'
          )
        : templateName;
      if (
        defaultTemplateId === template.id &&
        defaultTemplateCategory === category
      ) {
        highlightedTemplateName = `${highlightedTemplateName} <span style="color: red;">[Default]</span>`;
      }
      const isPresetTemplate = presetTemplates[category].some(
        (presetTemplate) => presetTemplate.id === template.id
      );

      return (
        <div key={template.id} className="template-item">
          <button
            className={`template-item-button ${
              expandedTemplate === template.displayName ? "active" : ""
            }`}
            onClick={() => toggleExpand(template.displayName)}
          >
            <span
              dangerouslySetInnerHTML={{ __html: highlightedTemplateName }}
            />
            <span className="template-item-arrow">
              {expandedTemplate === template.displayName ? "ᐯ" : "ᐳ"}
            </span>
          </button>
          {expandedTemplate === template.displayName && (
            <div className="template-item-content">
              {isPresetTemplate && (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <label>
                      <Switch
                        checked={
                          defaultTemplateId === template.id &&
                          defaultTemplateCategory === category
                        }
                        onChange={() =>
                          handleSetDefaultTemplate(
                            category,
                            template.id,
                            template.displayName
                          )
                        }
                      />
                      Default template
                    </label>
                  </div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      textAlign: "center",
                      fontStyle: "italic",
                      marginBottom: "1rem",
                    }}
                  >
                    Preset templates cannot be edited.
                  </div>
                </>
              )}
              {!isPresetTemplate && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <label>
                    <Switch
                      checked={
                        defaultTemplateId === template.id &&
                        defaultTemplateCategory === category
                      }
                      onChange={() =>
                        handleSetDefaultTemplate(
                          category,
                          template.id,
                          template.displayName
                        )
                      }
                    />
                    Default template
                  </label>
                </div>
              )}
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "500",
                  marginBottom: "0.5rem",
                  color: "var(--color-brand-dark-green)",
                }}
              >
                Name
              </div>
              {isEditingTemplateName === template.id && !isPresetTemplate ? (
                <input
                  className="box-sizing-default edit-template-textfield"
                  type="text"
                  defaultValue={template.displayName}
                  onBlur={(event) => {
                    setIsEditingTemplateName(null);
                    saveEditTemplateName(
                      category,
                      template.id,
                      event.target.value
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      setIsEditingTemplateName(null);
                      saveEditTemplateName(
                        category,
                        template.id,
                        (event.target as HTMLInputElement).value
                      );
                    }
                  }}
                />
              ) : (
                <span
                  onClick={() =>
                    !isPresetTemplate && setIsEditingTemplateName(template.id)
                  }
                >
                  {template.displayName}
                </span>
              )}
              <br />
              <br />
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "500",
                  marginBottom: "0.5rem",
                  color: "var(--color-brand-dark-green)",
                }}
              >
                Description
              </div>
              {isEditingTemplateDescription === template.id &&
              !isPresetTemplate ? (
                <input
                  className="box-sizing-default edit-template-textfield"
                  type="text"
                  defaultValue={template.description}
                  onBlur={(event) => {
                    setIsEditingTemplateDescription(null);
                    saveEditTemplateDescription(
                      category,
                      template.id,
                      event.target.value
                    );
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      setIsEditingTemplateDescription(null);
                      saveEditTemplateDescription(
                        category,
                        template.id,
                        (event.target as HTMLInputElement).value
                      );
                    }
                  }}
                />
              ) : (
                <span
                  onClick={() =>
                    !isPresetTemplate &&
                    setIsEditingTemplateDescription(template.id)
                  }
                >
                  {template.description !== ""
                    ? template.description
                    : "No description provided."}
                </span>
              )}
              <br />
              <br />
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "500",
                  marginBottom: "0.5rem",
                  color: "var(--color-brand-dark-green)",
                }}
              >
                Template
              </div>
              {isEditingTemplateContent === template.id && !isPresetTemplate ? (
                <textarea
                  className="box-sizing-default edit-template-textarea"
                  style={{ resize: "none", overflowY: "auto" }}
                  rows={template.visiblePrompt
                    .split("\n")
                    .reduce((totalRows, line) => {
                      return totalRows + Math.ceil(line.length / 60);
                    }, template.visiblePrompt.split("\n").length - 1)} // Adjust the row count based on content length and line breaks
                  defaultValue={template.visiblePrompt}
                  onInput={(event) => {
                    (event.target as HTMLTextAreaElement).style.height = "auto";
                    (event.target as HTMLTextAreaElement).style.height =
                      (event.target as HTMLTextAreaElement).scrollHeight + "px";
                  }}
                  onBlur={(event) => {
                    if (event.target.value.trim() !== "") {
                      setIsEditingTemplateContent(null);
                      saveEditTemplateContent(
                        category,
                        template.id,
                        template.displayName,
                        event.target.value
                      );
                    } else {
                      alert("Can't save an empty template.");
                    }
                  }}
                />
              ) : (
                <span
                  onClick={() =>
                    !isPresetTemplate &&
                    setIsEditingTemplateContent(template.id)
                  }
                  dangerouslySetInnerHTML={{
                    __html:
                      template.visiblePrompt !== ""
                        ? template.visiblePrompt.replace(/\n/g, "<br />")
                        : "No description provided.",
                  }}
                />
              )}
              {/* {isPresetTemplate ? (
                <div style={{ fontSize: "1rem" }}>{template.visiblePrompt}</div>
              ) : (
                <TipTap
                  content={DOMPurify.sanitize(template.visiblePrompt)}
                  alwaysFocus={false}
                  onSaveContent={(newContent) => {
                    saveEditTemplateContent(
                      category,
                      template.id,
                      template.name,
                      newContent
                    );
                  }}
                />
              )} */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "2rem",
                }}
              >
                <button
                  className="button-small-brand-red"
                  onClick={() => setIsDeleteTemplateConfirmationModalOpen(true)}
                  style={{
                    cursor: loadingDeleteTemplate ? "not-allowed" : "pointer",
                  }}
                  disabled={loadingDeleteTemplate}
                >
                  Delete Template
                </button>
                {isDeleteTemplateConfirmationModalOpen && (
                  <div className="box-sizing-default modal-background">
                    <div
                      className="box-sizing-default modals"
                      id="reset-password-modal"
                    >
                      <button
                        className="small-icon-button"
                        onClick={() => {
                          setIsDeleteTemplateConfirmationModalOpen(false);
                        }}
                      >
                        <img src={xMarkIcon} alt="close" />
                      </button>
                      <p>Are you sure you want to delete this template?</p>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "1rem",
                          marginTop: "1rem",
                        }}
                      >
                        <button
                          className="button-small-brand-green"
                          onClick={() => deleteTemplate(category, template.id)}
                        >
                          Yes
                        </button>
                        <button
                          className="button-small-brand-red"
                          onClick={() =>
                            setIsDeleteTemplateConfirmationModalOpen(false)
                          }
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  if (pageLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="box-sizing-default tab-content-container">
        <h2>Templates</h2>
        {Object.keys(presetTemplates).map((category) => (
          <div style={{ marginBottom: "3rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "1rem",
                paddingBottom: "0.2rem",
                borderBottom: "1px solid silver",
              }}
              key={category}
            >
              <div style={{ fontSize: "1.5rem", fontWeight: "500" }}>
                {camelCaseToWords(category)}
              </div>
              <button
                className="button-small-brand-green"
                style={{ padding: "0.2rem 1rem" }}
                onClick={() => {
                  setIsNewTemplateModalOpen(true);
                  setNewTemplateCategory(category as keyof Templates);
                }}
              >
                Add
              </button>
            </div>
            {renderTemplatesList(category as keyof Templates)}
          </div>
        ))}
      </div>
      {isNewTemplateModalOpen && renderAddNewTemplate()}
    </>
  );
};

export default TemplatesTabContent;
